import { Box, Typography } from '@mui/material'
import { type RefObject, useEffect, useRef, useState } from 'react'
import { type DashboardCount } from '../../../../models/peerToPeer/Dashboard'

const PAD = { left: 32, right: 12, top: 24, bottom: 28 }
const MAX_BAR_WIDTH = 88
const BAR_WIDTH_RATIO = 0.62
const BAR_COLOR = 'var(--light-blue-500)'

const useMeasuredWidth = (): [RefObject<HTMLDivElement>, number] => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (el == null) return
    setWidth(el.clientWidth)
    const observer = new ResizeObserver(entries => { setWidth(entries[0].contentRect.width) })
    observer.observe(el)
    return () => { observer.disconnect() }
  }, [])
  return [ref, width]
}

interface Props {
  data: DashboardCount[]
  formatKey: (key: string) => string
  height: number
}

/**
 * A self-contained SVG bar chart. MUI x-charts mis-binds the bar y-scale inside this nested
 * flex/overflow-hidden layout (bars render against a default [0,10] domain while the axis uses
 * the real data extent, so bars come out a fraction of their true height). This hand-rolled
 * version renders the intended design: teal bars, white value labels centred at each bar's top,
 * dashed horizontal gridlines, integer y-ticks with one unit of headroom, and x-axis labels.
 */
export const DashboardBarChart = ({ data, formatKey, height }: Props): JSX.Element => {
  const [ref, width] = useMeasuredWidth()

  const max = Math.max(...data.map(d => d.count), 1) + 1
  const plotWidth = Math.max(width - PAD.left - PAD.right, 0)
  const plotHeight = height - PAD.top - PAD.bottom
  const slot = data.length > 0 ? plotWidth / data.length : 0
  const barWidth = Math.min(slot * BAR_WIDTH_RATIO, MAX_BAR_WIDTH)
  const yOf = (value: number): number => PAD.top + plotHeight * (1 - value / max)
  const ticks = Array.from({ length: max + 1 }, (_, i) => i)

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', mb: '4px' }}>
        <Box sx={{ width: '14px', height: '14px', borderRadius: '2px', backgroundColor: BAR_COLOR }} />
        <Typography variant='body2'>Programs</Typography>
      </Box>
      <Box ref={ref} sx={{ width: '100%' }}>
        {width > 0 &&
          <svg width={width} height={height} role='img' aria-label='bar chart'>
            {ticks.map(tick => (
              <g key={tick}>
                <line x1={PAD.left} y1={yOf(tick)} x2={PAD.left + plotWidth} y2={yOf(tick)} stroke='var(--grey-300)' strokeDasharray='4 4' />
                <text x={PAD.left - 8} y={yOf(tick) + 4} textAnchor='end' fontSize='12' fill='var(--grey-500)'>{tick}</text>
              </g>
            ))}
            {data.map((d, index) => {
              const slotCenter = PAD.left + slot * index + slot / 2
              const barTop = yOf(d.count)
              return (
                <g key={d.key}>
                  <rect x={slotCenter - barWidth / 2} y={barTop} width={barWidth} height={PAD.top + plotHeight - barTop} rx='2' fill={BAR_COLOR} />
                  {d.count > 0 && <text x={slotCenter} y={barTop + 17} textAnchor='middle' fontSize='16' fontWeight='700' fill='var(--white)'>{d.count}</text>}
                  <text x={slotCenter} y={height - 8} textAnchor='middle' fontSize='12' fill='var(--grey-700)'>{formatKey(d.key)}</text>
                </g>
              )
            })}
          </svg>}
      </Box>
    </Box>
  )
}
